import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import InputGroup from '@bonprix/pattern-library/templates/input/Group.jsx';
import Label from '@bonprix/pattern-library/templates/input/Label.jsx';
import {articleOverviewStaticContext} from '../../ArticleOverviewStatic.jsx';
import {Constants} from '../../view-constants';
import {yieldToMain} from '../../ArticleOverviewHelper';

function Range({filterOptions, filterOptionChange, identifier, trackingId, unit, dropdown, mobileSubmitButton}) {
    const {texts} = useContext(articleOverviewStaticContext);

    const BASE_INPUT_PADDING = 24;
    const PADDING_PER_UNIT_CHAR = 10;

    const minRange = useRef(null);
    const maxRange = useRef(null);

    const previousMinRange = useRef(filterOptions[0].value);
    const previousMaxRange = useRef(filterOptions[1].value);

    const rangeFilterRef = useRef(null);

    const [invalid, setInvalid] = useState(false);
    const [noHits, setNoHits] = useState(false);

    useEffect(() => {
        if (unit.length > 1) {
            const paddingLeftValue = BASE_INPUT_PADDING + (unit.length - 1) * PADDING_PER_UNIT_CHAR;
            minRange.current.style.paddingLeft = `${paddingLeftValue}px`;
            maxRange.current.style.paddingLeft = `${paddingLeftValue}px`;
        }
    }, []);

    const checkRangeFilterValues = useCallback(
        async (closeKeyboard = false) => {
            const currentMinValue = minRange?.current?.value;
            const currentMaxValue = maxRange?.current?.value;

            if (previousMinRange.current === currentMinValue && previousMaxRange.current === currentMaxValue) {
                return;
            }

            previousMinRange.current = minRange.current.value;
            previousMaxRange.current = maxRange.current.value;

            const inputValid = checkIfRangeValuesAreValid(currentMinValue, currentMaxValue);
            let hasHits;

            let submitButton =
                dropdown?.current.dropdownContent?.querySelector('[jsh-dropdown-submit-button]') ||
                mobileSubmitButton?.current?.firstChild;

            // has to be checked before the fetch
            hasHits = await updateRangeAndCheckHits(submitButton);

            if (closeKeyboard && inputValid && hasHits) {
                // hide mobile keyboard
                maxRange.current.blur();
            } else if (!inputValid || !hasHits) {
                disableSubmitButton(submitButton);
            }

            setInvalid(!inputValid);
            setNoHits(!hasHits);
        },
        [minRange, maxRange, dropdown, mobileSubmitButton],
    );

    const handleInputChange = useCallback((event) => {
        event.preventDefault();
        const inputElement = event.currentTarget;

        inputElement.value = inputElement.value.replace(/[^0-9]/g, '');

        if (inputElement.value.length > inputElement.maxLength) {
            inputElement.value = inputElement.value.slice(0, inputElement.maxLength);
        }
    }, []);

    async function updateRangeAndCheckHits(submitButton) {
        // has to be checked before the fetch
        const dropdownButtonIsHovered = submitButton?.matches(':hover');
        const submitButtonIsActive = submitButton?.matches(':active');

        let hasHits;
        await updateRange().then((hits) => {
            hasHits = hits !== 0;
            if (!hasHits) {
                return;
            }
            if (dropdown?.current && dropdownButtonIsHovered) {
                yieldToMain(() => dropdown?.current.props.buttonOnClick());
            }
            if (submitButton && submitButtonIsActive) {
                setTimeout(() => {
                    submitButton.click();
                });
            }
        });
        return hasHits;
    }

    function disableSubmitButton(submitButton) {
        // disable submit button for half a second so that user can't submit filters without acknowledging an error
        const dropdownContentHtmlElement = dropdown?.current?.dropdownContent;
        submitButton?.setAttribute(Constants.DISABLE_ATTRIBUTE_NAME, 'true');
        dropdownContentHtmlElement?.setAttribute(Constants.DISABLE_ATTRIBUTE_NAME, 'true');
        setTimeout(() => {
            submitButton?.removeAttribute(Constants.DISABLE_ATTRIBUTE_NAME);
            dropdownContentHtmlElement?.removeAttribute(Constants.DISABLE_ATTRIBUTE_NAME);
        }, 500);
    }

    function checkIfRangeValuesAreValid(currentMinValue, currentMaxValue) {
        const firstValue = parseFloat(currentMinValue);
        const secondValue = parseFloat(currentMaxValue);

        return firstValue <= secondValue;
    }

    const attributesMinRange = useMemo(
        () => ({
            ref: minRange,
            min: 0,
            maxLength: 6,
            onBlur: () => checkRangeFilterValues(),
        }),
        [minRange, checkRangeFilterValues],
    );
    const attributesMaxRange = useMemo(
        () => ({
            ref: maxRange,
            min: 1,
            maxLength: 6,
            onBlur: () => checkRangeFilterValues(true),
        }),
        [maxRange, checkRangeFilterValues],
    );

    const invalidSuffix = invalid || noHits ? 'is-invalid' : '';
    const rangeLabel = `filter__range-label ${invalidSuffix}`;
    const fromToLabel = `input-group__label ${invalidSuffix}`;
    const invalidTextHint = () => {
        if (invalid) {
            return texts.invalid_range_input;
        }
        if (noHits) {
            return texts.no_hits_found;
        }
        return '';
    };

    return (
        <div className="filter__range" ref={rangeFilterRef}>
            <div className="filter__range-content">
                {(invalid || noHits) && (
                    <div className="filter__range-no-hits-text">
                        <span> {invalidTextHint()} </span>
                    </div>
                )}
                <span className="filter__range-inputs">
                    <InputGroup className={rangeLabel}>
                        <div className="input-group__field" jsh-input-group="" test-ref__filter-range_min-value="">
                            {renderPriceInput(filterOptions[0].value, attributesMinRange)}
                        </div>
                        <Label className={fromToLabel}>{texts.filter_range_from}</Label>
                    </InputGroup>

                    <InputGroup className={rangeLabel}>
                        <div className="input-group__field" jsh-input-group="" test-ref__filter-range_max-value="">
                            {renderPriceInput(filterOptions[1].value, attributesMaxRange)}
                        </div>
                        <Label className={fromToLabel}>{texts.filter_range_to}</Label>
                    </InputGroup>
                </span>
            </div>
        </div>
    );

    function renderPriceInput(defaultValue, attributes) {
        return (
            <>
                <span className="filter__range-currency" jsh-input-currency="">
                    {unit}&nbsp;
                </span>
                {renderInputField(defaultValue, attributes)}
            </>
        );
    }

    function renderInputField(defaultValue, attributes) {
        return (
            <input
                className="filter__range-input--text"
                type="tel"
                defaultValue={defaultValue}
                onInput={handleInputChange}
                {...attributes}
            />
        );
    }

    async function updateRange() {
        const value = `${minRange.current.value}<${maxRange.current.value}`;
        return await filterOptionChange(identifier, trackingId, [value], true);
    }
}

export default React.memo(Range);
